import { keyDomainFor, type SearchCredentials } from '@shared/core/search';
import { useMemo } from 'react';

import { type SearchClient, useSearchCredentials } from '../../entities/search';
import { ALGOLIA_PALETTE_COLLECTIONS } from '../constants/sections';
import { entitledRenderableSections } from '../utils/composeLegs';

/**
 * Palette availability — the app-wide search credentials alone decide it, on either engine.
 */
export interface GlobalSearchAvailability {
  /** True when the palette can drive live search: a minted key and at least one collection it can render. */
  enabled: boolean;
  /** The app-root search client, or null while the credentials allow no palette search. */
  client: SearchClient | null;
  /** Collection aliases the palette may search. */
  collections: string[];
  /** True while the first credential fetch for the current (user, org) is in flight. */
  loading: boolean;
}

function paletteCollections(credentials: SearchCredentials | null): string[] {
  switch (credentials?.engine) {
    case 'typesense':
      return [...(credentials.collections ?? [])];
    case 'algolia': {
      const entitled = new Set(credentials.collections);
      return ALGOLIA_PALETTE_COLLECTIONS.filter(
        (collection) => entitled.has(collection) && Boolean(credentials.keys[keyDomainFor(collection)])
      );
    }
    default:
      return [];
  }
}

export function useGlobalSearchEnabled(): GlobalSearchAvailability {
  const { credentials, state, client } = useSearchCredentials();

  return useMemo(() => {
    const loading = state === 'loading';
    const hasKey = Boolean(credentials?.keys.general) || Boolean(credentials?.keys.orders);
    const collections = paletteCollections(credentials);
    const searchable = entitledRenderableSections(collections).length > 0;
    const paletteClient = hasKey && searchable ? client : null;
    return { enabled: !loading && paletteClient !== null, client: paletteClient, collections, loading };
  }, [credentials, state, client]);
}
